import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Grid,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  FilterList,
  FileDownload,
  MoreVert,
  Edit,
  Delete,
  Add,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import apiService from '../../services/apiService';

interface Category {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  color: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface SubscriptionType {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface Frequency {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  value: number;
  unit: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface Subscription {
  id: number;
  name: string;
  subscription_number: string;
  description?: string;
  category_id: number;
  subscription_type_id: number;
  frequency_id: number;
  price: number;
  validity_period: number;
  status: 'active' | 'inactive' | 'suspended';
  is_featured: boolean;
  features?: string[];
  created_at: string;
  updated_at: string;
  category?: Category;
  subscription_type?: SubscriptionType;
  frequency?: Frequency;
}

const Subscriptions: React.FC = () => {
  const [currentView, setCurrentView] = useState<'subscriptions' | 'categories' | 'subscription-types' | 'frequencies'>('subscriptions');
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subscriptionTypes, setSubscriptionTypes] = useState<SubscriptionType[]>([]);
  const [frequencies, setFrequencies] = useState<Frequency[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  // Drawer states
  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [editingItemId, setEditingItemId] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
  }, [currentView]);

  const fetchData = async () => {
    try {
      setLoading(true);
      switch (currentView) {
        case 'subscriptions':
          // For subscriptions view, we need all data for dropdowns
          await Promise.all([
            fetchSubscriptions(),
            fetchCategories(),
            fetchSubscriptionTypes(),
            fetchFrequencies()
          ]);
          break;
        case 'categories':
          await fetchCategories();
          break;
        case 'subscription-types':
          await fetchSubscriptionTypes();
          break;
        case 'frequencies':
          await fetchFrequencies();
          break;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    const response = await apiService.getSubscriptions();
    setSubscriptions((response.data as any).data || response.data);
  };

  const fetchCategories = async () => {
    try {
      console.log('Fetching categories...');
      const response = await apiService.getCategories();
      console.log('Categories response:', response);
      setCategories((response.data as any).data || response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSubscriptionTypes = async () => {
    try {
      console.log('Fetching subscription types...');
      const response = await apiService.getSubscriptionTypes();
      console.log('Subscription types response:', response);
      setSubscriptionTypes((response.data as any).data || response.data);
    } catch (error) {
      console.error('Error fetching subscription types:', error);
    }
  };

  const fetchFrequencies = async () => {
    try {
      console.log('Fetching frequencies...');
      const response = await apiService.getFrequencies();
      console.log('Frequencies response:', response);
      setFrequencies((response.data as any).data || response.data);
    } catch (error) {
      console.error('Error fetching frequencies:', error);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't set selectedItem to null here as we need it for edit/delete operations
  };

  const handleEditClick = () => {
    console.log('Edit clicked for item:', selectedItem);
    setFormData(selectedItem);
    setEditingItemId(selectedItem.id);
    setEditDrawerOpen(true);
    setAnchorEl(null); // Close the menu
  };

  const handleCreateSubmit = async () => {
    try {
      console.log('Form data being submitted:', formData);
      
      // Validate required fields for subscriptions
      if (currentView === 'subscriptions') {
        if (!formData.name || !formData.subscription_number || !formData.category_id || 
            !formData.subscription_type_id || !formData.frequency_id || 
            !formData.price || !formData.validity_period) {
          toast.error('Please fill in all required fields');
          return;
        }
        
        // Ensure status is set
        const subscriptionData = {
          ...formData,
          status: formData.status || 'active'
        };
        console.log('Subscription data with status:', subscriptionData);
        await apiService.createSubscription(subscriptionData);
      } else {
        switch (currentView) {
          case 'categories':
            await apiService.createCategory(formData);
            break;
          case 'subscription-types':
            await apiService.createSubscriptionType(formData);
            break;
          case 'frequencies':
            await apiService.createFrequency(formData);
            break;
        }
      }
      
      console.log('Success: Item created successfully');
      toast.success(`${currentView} created successfully`);
      setAddDrawerOpen(false);
      setFormData({});
      fetchData();
    } catch (error) {
      console.error('Error creating item:', error);
      toast.error(`Failed to create ${currentView}`);
    }
  };

  const handleUpdateSubmit = async () => {
    try {
      console.log('Update form data being submitted:', formData);
      console.log('Editing item ID:', editingItemId);
      
      // Check if editingItemId exists
      if (!editingItemId) {
        console.error('No editing item ID found');
        toast.error('No item selected for update');
        return;
      }
      
      // Validate required fields for subscriptions
      if (currentView === 'subscriptions') {
        if (!formData.name || !formData.subscription_number || !formData.category_id || 
            !formData.subscription_type_id || !formData.frequency_id || 
            !formData.price || !formData.validity_period) {
          toast.error('Please fill in all required fields');
          return;
        }
        
        // Ensure status is set
        const subscriptionData = {
          ...formData,
          status: formData.status || 'active'
        };
        console.log('Subscription update data with status:', subscriptionData);
        console.log('Updating subscription with ID:', editingItemId);
        await apiService.updateSubscription(editingItemId, subscriptionData);
      } else {
        switch (currentView) {
          case 'categories':
            await apiService.updateCategory(editingItemId, formData);
            break;
          case 'subscription-types':
            await apiService.updateSubscriptionType(editingItemId, formData);
            break;
          case 'frequencies':
            await apiService.updateFrequency(editingItemId, formData);
            break;
        }
      }
      
      console.log('Success: Item updated successfully');
      toast.success(`${currentView} updated successfully`);
      setEditDrawerOpen(false);
      setFormData({});
      setEditingItemId(null);
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error(`Failed to update ${currentView}`);
    }
  };

  const handleFormChange = (field: string, value: any) => {
    console.log('Form field changed:', field, value);
    setFormData((prev: any) => {
      const newData = {
        ...prev,
        [field]: value
      };
      console.log('Updated form data:', newData);
      return newData;
    });
  };

  const handleDeleteClick = async () => {
    if (!selectedItem) return;
    
    setAnchorEl(null); // Close the menu
    
    const itemName = selectedItem.name || selectedItem.display_name || 'item';
    
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${itemName}". This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'rounded-lg',
        confirmButton: 'rounded-md',
        cancelButton: 'rounded-md'
      }
    });

    if (result.isConfirmed) {
      try {
        switch (currentView) {
          case 'subscriptions':
            await apiService.deleteSubscription(selectedItem.id);
            break;
          case 'categories':
            await apiService.deleteCategory(selectedItem.id);
            break;
          case 'subscription-types':
            await apiService.deleteSubscriptionType(selectedItem.id);
            break;
          case 'frequencies':
            await apiService.deleteFrequency(selectedItem.id);
            break;
        }
        
        Swal.fire({
          title: 'Deleted!',
          text: 'The item has been deleted successfully.',
          icon: 'success',
          confirmButtonColor: '#10b981',
          customClass: {
            popup: 'rounded-lg',
            confirmButton: 'rounded-md'
          }
        });
        
        fetchData();
      } catch (error) {
        console.error('Error deleting item:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete the item. Please try again.',
          icon: 'error',
          confirmButtonColor: '#dc2626',
          customClass: {
            popup: 'rounded-lg',
            confirmButton: 'rounded-md'
          }
        });
      } finally {
        setSelectedItem(null);
      }
    }
  };

  const getStatusCount = (status: string) => {
    return subscriptions.filter(sub => sub.status === status).length;
  };

  const getActiveCount = () => {
    switch (currentView) {
      case 'subscriptions':
        return subscriptions.filter(sub => sub.status === 'active').length;
      case 'categories':
        return categories.filter(cat => cat.is_active).length;
      case 'subscription-types':
        return subscriptionTypes.filter(type => type.is_active).length;
      case 'frequencies':
        return frequencies.filter(freq => freq.is_active).length;
      default:
        return 0;
    }
  };

  const getTotalCount = () => {
    switch (currentView) {
      case 'subscriptions':
        return subscriptions.length;
      case 'categories':
        return categories.length;
      case 'subscription-types':
        return subscriptionTypes.length;
      case 'frequencies':
        return frequencies.length;
      default:
        return 0;
    }
  };

  const renderTable = () => {
    switch (currentView) {
      case 'subscriptions':
        return renderSubscriptionsTable();
      case 'categories':
        return renderCategoriesTable();
      case 'subscription-types':
        return renderSubscriptionTypesTable();
      case 'frequencies':
        return renderFrequenciesTable();
      default:
        return null;
    }
  };

  const renderSubscriptionsTable = () => (
    <Table>
      <TableHead sx={{ backgroundColor: '#f8fafc' }}>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Name</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Category</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Type</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Frequency</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Price</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151', textAlign: 'center' }}>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {subscriptions
          .filter(item => 
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.subscription_number.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((subscription) => (
            <TableRow key={subscription.id} hover>
              <TableCell>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    {subscription.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    {subscription.subscription_number}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={subscription.category?.display_name || 'N/A'}
                  size="small"
                  sx={{
                    backgroundColor: subscription.category?.color || '#e5e7eb',
                    color: 'white',
                    fontWeight: 500,
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  {subscription.subscription_type?.display_name || 'N/A'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  {subscription.frequency?.display_name || 'N/A'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  AED {Number(subscription.price || 0).toFixed(2)}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={subscription.status}
                  size="small"
                  color={
                    subscription.status === 'active' ? 'success' :
                    subscription.status === 'inactive' ? 'default' : 'warning'
                  }
                />
              </TableCell>
              <TableCell sx={{ textAlign: 'center' }}>
                <IconButton
                  onClick={(e) => handleMenuOpen(e, subscription)}
                  sx={{ color: '#6b7280' }}
                >
                  <MoreVert />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );

  const renderCategoriesTable = () => (
    <Table>
      <TableHead sx={{ backgroundColor: '#f8fafc' }}>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Name</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Display Name</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Description</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Color</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151', textAlign: 'center' }}>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {categories
          .filter(item => 
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.display_name.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((category) => (
            <TableRow key={category.id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {category.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  {category.display_name}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  {category.description || 'No description'}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '4px',
                      backgroundColor: category.color,
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    {category.color}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={category.is_active ? 'Active' : 'Inactive'}
                  size="small"
                  color={category.is_active ? 'success' : 'default'}
                />
              </TableCell>
              <TableCell sx={{ textAlign: 'center' }}>
                <IconButton
                  onClick={(e) => handleMenuOpen(e, category)}
                  sx={{ color: '#6b7280' }}
                >
                  <MoreVert />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );

  const renderSubscriptionTypesTable = () => (
    <Table>
      <TableHead sx={{ backgroundColor: '#f8fafc' }}>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Name</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Display Name</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Description</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151', textAlign: 'center' }}>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {subscriptionTypes
          .filter(item => 
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.display_name.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((type) => (
            <TableRow key={type.id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {type.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  {type.display_name}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  {type.description || 'No description'}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={type.is_active ? 'Active' : 'Inactive'}
                  size="small"
                  color={type.is_active ? 'success' : 'default'}
                />
              </TableCell>
              <TableCell sx={{ textAlign: 'center' }}>
                <IconButton
                  onClick={(e) => handleMenuOpen(e, type)}
                  sx={{ color: '#6b7280' }}
                >
                  <MoreVert />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );

  const renderFrequenciesTable = () => (
    <Table>
      <TableHead sx={{ backgroundColor: '#f8fafc' }}>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Name</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Display Name</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Value</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Unit</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#374151', textAlign: 'center' }}>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {frequencies
          .filter(item => 
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.display_name.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((frequency) => (
            <TableRow key={frequency.id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {frequency.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  {frequency.display_name}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {frequency.value}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  {frequency.unit}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={frequency.is_active ? 'Active' : 'Inactive'}
                  size="small"
                  color={frequency.is_active ? 'success' : 'default'}
                />
              </TableCell>
              <TableCell sx={{ textAlign: 'center' }}>
                <IconButton
                  onClick={(e) => handleMenuOpen(e, frequency)}
                  sx={{ color: '#6b7280' }}
                >
                  <MoreVert />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );

  return (
    <Box sx={{ p: 3, backgroundColor: '#f0f4f8', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Subscriptions Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={async () => {
            // Ensure all data is loaded for dropdowns
            if (currentView === 'subscriptions') {
              await Promise.all([
                fetchCategories(),
                fetchSubscriptionTypes(),
                fetchFrequencies()
              ]);
            }
            
            // Initialize form data with default values
            setFormData({
              name: '',
              display_name: '',
              description: '',
              color: '#000000',
              value: '',
              unit: 'months',
              subscription_number: '#SUB' + Date.now(),
              category_id: '',
              subscription_type_id: '',
              frequency_id: '',
              price: '',
              validity_period: '',
              status: 'active',
              is_featured: false,
              features: []
            });
            
            setAddDrawerOpen(true);
          }}
          sx={{
            backgroundColor: '#4A90E2',
            borderRadius: '8px',
            px: 3,
            py: 1,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            height: '40px',
            '&:hover': {
              backgroundColor: '#3b82f6',
            },
          }}
        >
          Add New
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Left Sidebar - Sub Menu */}
        <Box sx={{ width: '25%', minWidth: '300px' }}>
          <Card sx={{ 
            borderRadius: '12px', 
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            height: 'calc(100vh - 160px)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                Sub Menu
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  { key: 'subscriptions', label: 'Subscriptions', icon: '📋' },
                  { key: 'categories', label: 'Categories', icon: '🏷️' },
                  { key: 'subscription-types', label: 'Subscription Types', icon: '📝' },
                  { key: 'frequencies', label: 'Frequencies', icon: '⏰' },
                ].map((item) => (
                  <Button
                    key={item.key}
                    onClick={() => setCurrentView(item.key as any)}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: currentView === item.key ? '#e0f2fe' : 'transparent',
                      color: currentView === item.key ? '#0369a1' : '#6b7280',
                      fontWeight: currentView === item.key ? 600 : 400,
                      '&:hover': {
                        backgroundColor: currentView === item.key ? '#e0f2fe' : '#f8fafc',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ fontSize: '1.2rem' }}>{item.icon}</Typography>
                      <Typography variant="body2">{item.label}</Typography>
                    </Box>
                  </Button>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Right Content Area */}
        <Box sx={{ flex: 1, height: 'calc(100vh - 200px)' }}>
          {/* Overview Cards */}
          <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em'
                  }}>
                    Total {currentView === 'subscriptions' ? 'Subscriptions' : 
                           currentView === 'categories' ? 'Categories' :
                           currentView === 'subscription-types' ? 'Subscription Types' : 'Frequencies'}
                  </Typography>
                  <Typography variant="h2" sx={{ 
                    color: '#1e40af', 
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}>
                    {getTotalCount()}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em'
                  }}>
                    Active {currentView === 'subscriptions' ? 'Subscriptions' : 
                           currentView === 'categories' ? 'Categories' :
                           currentView === 'subscription-types' ? 'Subscription Types' : 'Frequencies'}
                  </Typography>
                  <Typography variant="h2" sx={{ 
                    color: '#1e40af', 
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}>
                    {getActiveCount()}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em'
                  }}>
                    New This Month
                  </Typography>
                  <Typography variant="h2" sx={{ 
                    color: '#1e40af', 
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}>
                    {getTotalCount()}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

      {/* Search and Filter Section */}
      <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <TextField
                fullWidth
                placeholder={`Search ${currentView}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#9ca3af', fontSize: '1.2rem' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#f9fafb',
                    height: '40px',
                  },
                }}
              />
            </Box>
            <Box sx={{ flex: '0 0 150px' }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                sx={{
                  borderRadius: '8px',
                  borderColor: '#d1d5db',
                  color: '#374151',
                  textTransform: 'none',
                  width: '100%',
                  height: '40px',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: '#f9fafb',
                  },
                }}
              >
                Filter
              </Button>
            </Box>
            <Box sx={{ flex: '0 0 150px', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                sx={{
                  borderRadius: '8px',
                  borderColor: '#d1d5db',
                  color: '#374151',
                  textTransform: 'none',
                  height: '40px',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: '#f9fafb',
                  },
                }}
              >
                Export
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card sx={{ 
        borderRadius: '12px', 
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        height: 'calc(100vh - 380px)'
      }}>
        <TableContainer sx={{ 
          borderRadius: '12px',
          height: 'calc(100vh - 440px)',
          overflow: 'auto'
        }}>
          {renderTable()}
        </TableContainer>
        <TablePagination
          component="div"
          count={currentView === 'subscriptions' ? subscriptions.length : 
                 currentView === 'categories' ? categories.length :
                 currentView === 'subscription-types' ? subscriptionTypes.length : frequencies.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <MenuItem onClick={handleEditClick} sx={{ gap: 2 }}>
          <Edit sx={{ fontSize: '1.2rem', color: '#6b7280' }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ gap: 2, color: '#dc2626' }}>
          <Delete sx={{ fontSize: '1.2rem' }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Add Drawer */}
      <Drawer
        anchor="right"
        open={addDrawerOpen}
        onClose={() => setAddDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: '500px',
            padding: '24px',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b' }}>
            Add {currentView === 'subscriptions' ? 'Subscription' : 
                   currentView === 'categories' ? 'Category' :
                   currentView === 'subscription-types' ? 'Subscription Type' : 'Frequency'}
          </Typography>
          <IconButton onClick={() => setAddDrawerOpen(false)}>
            ×
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {currentView === 'subscriptions' && (
            <>
              <TextField
                fullWidth
                label="Subscription Name"
                variant="outlined"
                placeholder="Enter subscription name"
                sx={{ '& .MuiOutlinedInput-root': { borderColor: '#10b981' } }}
                value={formData.name || ''}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
              
              <TextField
                fullWidth
                label="Subscription Number"
                variant="outlined"
                placeholder="Enter subscription number"
                value={formData.subscription_number || ''}
                onChange={(e) => handleFormChange('subscription_number', e.target.value)}
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  variant="outlined"
                  value={formData.category_id || ''}
                  onChange={(e) => handleFormChange('category_id', e.target.value)}
                  SelectProps={{
                    native: false,
                  }}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </TextField>
                
                <TextField
                  fullWidth
                  select
                  label="Subscription Type"
                  variant="outlined"
                  value={formData.subscription_type_id || ''}
                  onChange={(e) => handleFormChange('subscription_type_id', e.target.value)}
                  SelectProps={{
                    native: false,
                  }}
                >
                  {subscriptionTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  select
                  label="Frequency"
                  variant="outlined"
                  value={formData.frequency_id || ''}
                  onChange={(e) => handleFormChange('frequency_id', e.target.value)}
                  SelectProps={{
                    native: false,
                  }}
                >
                  {frequencies.map((frequency) => (
                    <MenuItem key={frequency.id} value={frequency.id}>
                      {frequency.name}
                    </MenuItem>
                  ))}
                </TextField>
                
                <TextField
                  fullWidth
                  label="Price"
                  type="number"
                  variant="outlined"
                  placeholder="Enter price"
                  value={formData.price || ''}
                  onChange={(e) => handleFormChange('price', e.target.value)}
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Validity Period"
                  type="number"
                  variant="outlined"
                  placeholder="Enter validity period in days"
                  value={formData.validity_period || ''}
                  onChange={(e) => handleFormChange('validity_period', e.target.value)}
                />
                
                <TextField
                  fullWidth
                  select
                  label="Status"
                  variant="outlined"
                  value={formData.status || 'active'}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </TextField>
              </Box>
              
              <TextField
                fullWidth
                label="Description (optional)"
                variant="outlined"
                multiline
                rows={3}
                placeholder="Enter description"
                value={formData.description || ''}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </>
          )}
          
          {currentView === 'categories' && (
            <>
              <TextField
                fullWidth
                label="Name"
                variant="outlined"
                placeholder="Enter name"
                value={formData.name || ''}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Display Name"
                variant="outlined"
                placeholder="Enter display name"
                value={formData.display_name || ''}
                onChange={(e) => handleFormChange('display_name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Description"
                variant="outlined"
                multiline
                rows={3}
                placeholder="Enter description"
                value={formData.description || ''}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
              <TextField
                fullWidth
                label="Color"
                type="color"
                variant="outlined"
                value={formData.color || '#000000'}
                onChange={(e) => handleFormChange('color', e.target.value)}
              />
            </>
          )}
          
          {currentView === 'subscription-types' && (
            <>
              <TextField
                fullWidth
                label="Name"
                variant="outlined"
                placeholder="Enter name"
                value={formData.name || ''}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Display Name"
                variant="outlined"
                placeholder="Enter display name"
                value={formData.display_name || ''}
                onChange={(e) => handleFormChange('display_name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Description"
                variant="outlined"
                multiline
                rows={3}
                placeholder="Enter description"
                value={formData.description || ''}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </>
          )}
          
          {currentView === 'frequencies' && (
            <>
              <TextField
                fullWidth
                label="Name"
                variant="outlined"
                placeholder="Enter name"
                value={formData.name || ''}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Display Name"
                variant="outlined"
                placeholder="Enter display name"
                value={formData.display_name || ''}
                onChange={(e) => handleFormChange('display_name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Value"
                type="number"
                variant="outlined"
                placeholder="Enter value"
                value={formData.value || ''}
                onChange={(e) => handleFormChange('value', e.target.value)}
              />
              <TextField
                fullWidth
                select
                label="Unit"
                variant="outlined"
                value={formData.unit || 'months'}
                onChange={(e) => handleFormChange('unit', e.target.value)}
              >
                <MenuItem value="days">Days</MenuItem>
                <MenuItem value="weeks">Weeks</MenuItem>
                <MenuItem value="months">Months</MenuItem>
                <MenuItem value="years">Years</MenuItem>
              </TextField>
              <TextField
                fullWidth
                label="Description"
                variant="outlined"
                multiline
                rows={3}
                placeholder="Enter description"
                value={formData.description || ''}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => setAddDrawerOpen(false)}
            sx={{ px: 3 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            sx={{ px: 3, backgroundColor: '#4A90E2' }}
          >
            Create
          </Button>
        </Box>
      </Drawer>

      {/* Edit Drawer */}
      <Drawer
        anchor="right"
        open={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: '500px',
            padding: '24px',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b' }}>
            Edit {currentView === 'subscriptions' ? 'Subscription' : 
                   currentView === 'categories' ? 'Category' :
                   currentView === 'subscription-types' ? 'Subscription Type' : 'Frequency'}
          </Typography>
          <IconButton onClick={() => setEditDrawerOpen(false)}>
            ×
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {currentView === 'subscriptions' && (
            <>
              <TextField
                fullWidth
                label="Subscription Name"
                variant="outlined"
                placeholder="Enter subscription name"
                value={formData.name || ''}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
              
              <TextField
                fullWidth
                label="Subscription Number"
                variant="outlined"
                placeholder="Enter subscription number"
                value={formData.subscription_number || ''}
                onChange={(e) => handleFormChange('subscription_number', e.target.value)}
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  variant="outlined"
                  value={formData.category_id || ''}
                  onChange={(e) => handleFormChange('category_id', e.target.value)}
                  SelectProps={{
                    native: false,
                  }}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </TextField>
                
                <TextField
                  fullWidth
                  select
                  label="Subscription Type"
                  variant="outlined"
                  value={formData.subscription_type_id || ''}
                  onChange={(e) => handleFormChange('subscription_type_id', e.target.value)}
                  SelectProps={{
                    native: false,
                  }}
                >
                  {subscriptionTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  select
                  label="Frequency"
                  variant="outlined"
                  value={formData.frequency_id || ''}
                  onChange={(e) => handleFormChange('frequency_id', e.target.value)}
                  SelectProps={{
                    native: false,
                  }}
                >
                  {frequencies.map((frequency) => (
                    <MenuItem key={frequency.id} value={frequency.id}>
                      {frequency.name}
                    </MenuItem>
                  ))}
                </TextField>
                
                <TextField
                  fullWidth
                  label="Price"
                  type="number"
                  variant="outlined"
                  placeholder="Enter price"
                  value={formData.price || ''}
                  onChange={(e) => handleFormChange('price', e.target.value)}
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Validity Period"
                  type="number"
                  variant="outlined"
                  placeholder="Enter validity period in days"
                  value={formData.validity_period || ''}
                  onChange={(e) => handleFormChange('validity_period', e.target.value)}
                />
                
                <TextField
                  fullWidth
                  select
                  label="Status"
                  variant="outlined"
                  value={formData.status || 'active'}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </TextField>
              </Box>
              
              <TextField
                fullWidth
                label="Description (optional)"
                variant="outlined"
                multiline
                rows={3}
                placeholder="Enter description"
                value={formData.description || ''}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </>
          )}
          
          {currentView === 'categories' && (
            <>
              <TextField
                fullWidth
                label="Name"
                variant="outlined"
                placeholder="Enter name"
                value={formData.name || ''}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Display Name"
                variant="outlined"
                placeholder="Enter display name"
                value={formData.display_name || ''}
                onChange={(e) => handleFormChange('display_name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Description"
                variant="outlined"
                multiline
                rows={3}
                placeholder="Enter description"
                value={formData.description || ''}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
              <TextField
                fullWidth
                label="Color"
                type="color"
                variant="outlined"
                value={formData.color || '#000000'}
                onChange={(e) => handleFormChange('color', e.target.value)}
              />
            </>
          )}
          
          {currentView === 'subscription-types' && (
            <>
              <TextField
                fullWidth
                label="Name"
                variant="outlined"
                placeholder="Enter name"
                value={formData.name || ''}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Display Name"
                variant="outlined"
                placeholder="Enter display name"
                value={formData.display_name || ''}
                onChange={(e) => handleFormChange('display_name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Description"
                variant="outlined"
                multiline
                rows={3}
                placeholder="Enter description"
                value={formData.description || ''}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </>
          )}
          
          {currentView === 'frequencies' && (
            <>
              <TextField
                fullWidth
                label="Name"
                variant="outlined"
                placeholder="Enter name"
                value={formData.name || ''}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Display Name"
                variant="outlined"
                placeholder="Enter display name"
                value={formData.display_name || ''}
                onChange={(e) => handleFormChange('display_name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Value"
                type="number"
                variant="outlined"
                placeholder="Enter value"
                value={formData.value || ''}
                onChange={(e) => handleFormChange('value', e.target.value)}
              />
              <TextField
                fullWidth
                select
                label="Unit"
                variant="outlined"
                value={formData.unit || 'months'}
                onChange={(e) => handleFormChange('unit', e.target.value)}
              >
                <MenuItem value="days">Days</MenuItem>
                <MenuItem value="weeks">Weeks</MenuItem>
                <MenuItem value="months">Months</MenuItem>
                <MenuItem value="years">Years</MenuItem>
              </TextField>
              <TextField
                fullWidth
                label="Description"
                variant="outlined"
                multiline
                rows={3}
                placeholder="Enter description"
                value={formData.description || ''}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => setEditDrawerOpen(false)}
            sx={{ px: 3 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateSubmit}
            sx={{ px: 3, backgroundColor: '#4A90E2' }}
          >
            Update
          </Button>
        </Box>
      </Drawer>

        </Box>
      </Box>
    </Box>
  );
};

export default Subscriptions;